import React, { useState } from "react"
import gql from 'graphql-tag'
import { useAllOrdersQuery, AllOrdersQueryVariables, OrderFilterInput, useOrderBulkDispatchMutation } from "../../../generated/graphql"
import Modal from "../../Modal/Modal"
import styles from './OrderDispatchExport.module.scss'
import Button, { ButtonType } from "../../General/Button/Button"
import ContactTile from "../../Tiles/ContactTile/ContactTile"
import { Send } from "react-feather"
import fetchPdfToken from "../../../helpers/pdfAuthConfig"
import { useAuth } from "../../../context/AuthContext"

export const GetOrderForExport = gql`
    fragment singleOrderExport on Order {
        carrier {
           name
           email
           fax
        }
        customer {
           name
        }
        tripNumber
        deliveryDate
        location {
           directions
        }
        terminal {
           name
           directions
        }
        specialInstructions
        supplier {
           name
        }
        poNumber
        miles
    }

    query GetSingleOrderForExport($id: Int!) {
        orderGetSingle(id: $id) {
            ...singleOrderExport
        }
    }
`

export const BulkDispatchOrdersMutation = gql`
    mutation orderBulkDispatch($ids: [Int!], $sendEmail: Boolean!) {
        orderBulkDispatch(ids: $ids, sendEmail: $sendEmail)
    }
`

interface Props {
   orderKeys: number[]
   closeModal(): void
   callback?(): void
}

const OrderDispatchExportModal = (props: Props) => {
   const [dispatch, setDispatch] = useState(false)
   const [dispatchSubmitted, setDispatchSubmitted] = useState(false)
   const { msalInstance, account, authority, logout } = useAuth();


   const [cursor] = useState("LTE=")
   const pageSize = 25
   const filter: OrderFilterInput = {
      orderKey: { in: props.orderKeys }
   }

   const variables: AllOrdersQueryVariables = {
      cursor: cursor,
      pageSize,
      filterObject: filter
   }

   const { data, loading, error } = useAllOrdersQuery({ variables })

   const [bulkDispatchOrderMutation] = useOrderBulkDispatchMutation()

   const [errorMessage, setErrorMessage] = useState<string>("")
   const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

   if (loading) return <Modal headerText="Order Dispatch">Loading...</Modal>
   if (error) return <Modal><h2>Error</h2></Modal>

   const submit = (sendEmail: boolean) => {
      console.log(`[OrderDispatch] Starting dispatch process for ${props.orderKeys.length} orders`, {
         orderKeys: props.orderKeys,
         sendEmail,
         timestamp: new Date().toISOString()
      });

      setErrorMessage(null)
      setIsSubmitting(true)

      const startTime = performance.now();
      console.log(`[OrderDispatch] Calling bulkDispatchOrderMutation...`);

      bulkDispatchOrderMutation({ variables: { ids: props.orderKeys, sendEmail } })
         .then(res => {
            const endTime = performance.now();
            const duration = endTime - startTime;

            console.log(`[OrderDispatch] Bulk dispatch mutation completed in ${duration.toFixed(2)}ms`, {
               success: !!res.data.orderBulkDispatch,
               result: res.data.orderBulkDispatch,
               duration
            });

            if (!res.data.orderBulkDispatch) {
               console.error(`[OrderDispatch] Dispatch failed - orderBulkDispatch returned false`);
               setErrorMessage("Failed to send email")
               setIsSubmitting(false)
            }
            else {
               console.log(`[OrderDispatch] Dispatch completed successfully`);
               setDispatchSubmitted(true)
            }
         })
         .catch(err => {
            const endTime = performance.now();
            const duration = endTime - startTime;

            console.error(`[OrderDispatch] Bulk dispatch mutation failed after ${duration.toFixed(2)}ms`, {
               error: err.message,
               stack: err.stack,
               duration
            });

            setErrorMessage("Failed to send email")
            setIsSubmitting(false)
         })
   }

   const dispatchOrder = () => {
      console.log(`[OrderDispatch] User clicked 'Dispatch Without Sending' for orders:`, props.orderKeys);
      submit(false)
   }

   const dispatchOrderAndEmail = () => {
      console.log(`[OrderDispatch] User clicked 'Dispatch & Send' for orders:`, props.orderKeys);
      setDispatch(false)
      submit(true)
   }

   const ok = () => {
      props.callback()
      props.closeModal()
   }

   const footerLeftContent = !dispatchSubmitted && (<Button clickAction={props.closeModal} buttonType={ButtonType.Secondary}>Cancel</Button>)
   const footerRightContent = !dispatchSubmitted ? (<>
      <Button clickAction={dispatchOrder} buttonType={ButtonType.Primary} isDisabled={isSubmitting}>Dispatch Without Sending</Button>
      <Button clickAction={_ => setDispatch(true)} buttonType={ButtonType.Success} isDisabled={isSubmitting}><Send size={18} /> Dispatch & Send</Button>
   </>
   ) : <Button clickAction={ok} buttonType={ButtonType.Primary}>Ok</Button>

   const carrierKeys = [...new Set(data.orderGetAll.edges.map(o => o.node.carrier.carrierKey))]
   const uniqueCarrierOrders = carrierKeys.map(carrierKey => data.orderGetAll.edges.find(o => o.node.carrier.carrierKey === carrierKey)).map(x => x.node)

   const getOrderKeysFromCarrierKey = (carrierKey: number) => {
      return data.orderGetAll.edges.filter(o => o.node.carrier.carrierKey === carrierKey).map(o => o.node.orderKey)
   }

   const goToPdfView = async (carrierKey: number) => {
      const orderKeys = getOrderKeysFromCarrierKey(carrierKey)

      const token = await fetchPdfToken(msalInstance, account, authority, logout);
      if (!token) {
         alert('Failed to generate token for PDF export.');
         return;
      }

      window.open(window.location.origin + `/api/orderExport?orderKeys=${orderKeys.join(',')}&token=${token}`)

   }

   const confirmLeftFooter = <Button clickAction={_ => setDispatch(false)} buttonType={ButtonType.Secondary}>Cancel</Button>
   const confirmRightFooter = <Button clickAction={dispatchOrderAndEmail} buttonType={ButtonType.Success}>Yes</Button>

   return (
      <>
         {dispatch && <Modal headerText="Dispatch?" footerLeftContent={confirmLeftFooter} footerRightContent={confirmRightFooter}>
            <p>Are you sure you want to send an email for the dispatched orders?</p>
         </Modal>
         }
         {!dispatch && <Modal headerText="Order Dispatch Export" footerLeftContent={footerLeftContent} footerRightContent={footerRightContent}>
            {errorMessage && <p className={styles.orderDispatchExport__errorMessage}>{errorMessage}</p>}
            {dispatchSubmitted && <p>Successful Dispatch</p>}
            <div className="row">
               {uniqueCarrierOrders && uniqueCarrierOrders.map(order => {
                  return <div className="col-md-6" key={order.orderKey}>
                     <ContactTile firstName={order.carrier.name} email={order.carrier.email}></ContactTile>
                     <Button buttonType={ButtonType.Secondary} clickAction={() => goToPdfView(order.carrier.carrierKey)}>Preview PDF</Button>
                  </div>
               })}


            </div>
         </Modal >}
      </>
   )
}
export default OrderDispatchExportModal
